import React, { useState } from 'react';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Form,
  FormGroup,
  Input,
  Row,
  Col,
  FormFeedback,
  InputGroup, InputGroupText,
  Label,
  TabContent,
  TabPane,
  Nav,
  NavItem,
  NavLink,
} from 'reactstrap';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import {
  ONLY_STRING,
  TEN_DIGIT_PHONE_NUMBER_REGEX,
} from '../../../../utils/constants/ValidationRegexs';
import {
  authHeader,
  authfileHeader,
} from '../../../../utils/functions/CoreFunctions';
import {
  AGENCY_USER_CREATE_URL,
  AGENCY_USER_UPDATE_URL,
  FILE_UPLOAD_URL,
} from '../../../../utils/APIEndpoints';
import API from '../../../../redux/actions/API';
import Swal from 'sweetalert2';
import {
  getAlertToast,
  getCurrentUserId,
  SingleStringFirstLetterCaps,
} from '../../../../utils/functions/CustomFunctions';
import { useParams } from 'react-router-dom';
import defaultAvatar from '../../../../assets/images/defaultImages/defaultAvatar.png';
import CustomLoader from '../../../common/CustomLoader';
import { useSelector, useDispatch } from 'react-redux';
import { SET_AGENCY_USER_PROFILE_PICTURE } from '../../../../redux/actions/ActionTypes';
import AdminPasswordControl from './AdminPasswordControl';

const validationSchema = Yup.object({
  firstName: Yup.string()
    .trim()
    .required('First name is required')
    .matches(ONLY_STRING, 'Only letters allowed'),
  lastName: Yup.string()
    .trim()
    .required('Last name is required')
    .matches(ONLY_STRING, 'Only letters allowed'),
  email: Yup.string()
    .trim()
    .required('Email is required')
    .email('Enter valid email'),
  phoneNumber: Yup.string()
    .trim()
    .required('Phone number is required')
    .matches(TEN_DIGIT_PHONE_NUMBER_REGEX, 'Enter valid phone number'),
  address: Yup.string().trim().required('Address is required'),
  role: Yup.string().trim().required('Role is required'),
});

const Create = (props) => {
  const { toggle, modal, data, isAdd, saveCallBack } = props;

  const AgencyUserForm = useSelector((state) => state.AgencyUserForm);
  const dispatch = useDispatch();
  const { profileImage = '' } = AgencyUserForm;
  const [btnLoading, setBtnLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('1');

  const tabToogle = (tab) => {
    if (activeTab !== tab) setActiveTab(tab);
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    const formData = new FormData();
    formData.append('file', e.target.files[0]);
    const options = {
      method: 'POST',
      url: FILE_UPLOAD_URL,
      headers: authfileHeader(),
      data: formData,
    };
    try {
      Swal.fire(getAlertToast('success', 'Please wait...'));
      var apiResponse = await API(options);
      const { status, filepath } = apiResponse.data;
      if (status === 'Success') {
        dispatch({
          type: SET_AGENCY_USER_PROFILE_PICTURE,
          payload: filepath,
        });
      }
    } catch (error) {
      if (error.response) {
        Swal.fire(
          getAlertToast('error', 'File upload failed, please try again!')
        );
      }
    }
  };

  //Formik
  const {
    handleSubmit,
    handleChange,
    values,
    errors,
    touched,
    setFieldTouched,
    setFieldValue,
  } = useFormik({
    initialValues: {
      ...data,
    },
    enableReinitialize: true,
    validationSchema,
    onSubmit: (values) => saveFormValues(values),
  });

  let { id } = useParams();

  //On Submit
  const saveFormValues = async (values) => {
    values.createdBy = getCurrentUserId();
    values.agencyId = parseInt(id);
    values.profileImage = profileImage;
    if (isAdd) {
      values.hyperlink = window.location.origin + '/signin';
    } //Route Link For Login From Email
    const options = {
      method: isAdd ? 'POST' : 'PUT',
      url: isAdd ? AGENCY_USER_CREATE_URL : AGENCY_USER_UPDATE_URL,
      headers: authHeader(),
      data: values,
    };
    try {
      setBtnLoading(true);
      let response = await API(options);
      const { status, message } = response.data;
      if (status === 'Error') {
        Swal.fire(getAlertToast('error', message));
      } else {
        Swal.fire(
          getAlertToast(
            'success',
            isAdd
              ? 'New User is created and password is sent via email.'
              : 'Agency updated successfully'
          )
        );
        saveCallBack();
      }
    } catch (error) {
      if (error.response) {
        let { data } = error.response;
        Swal.fire(getAlertToast('error', data.message));
      }
    } finally {
      setBtnLoading(false);
    }
  };

  return (
    <div>
      <Modal
        isOpen={modal}
        toggle={toggle}
        style={{ maxWidth: '800px' }}
        backdrop="static"
        className='popup'
      >
        <ModalHeader toggle={toggle}>
          {isAdd ? 'Create Agency User' : 'Update Agency User'}
        </ModalHeader>
        <Nav tabs style={{ margin: '0.5rem 0.5rem' }}>
          <NavItem>
            <NavLink
              className={activeTab === '1' ? 'active' : ''}
              onClick={() => {
                tabToogle('1');
              }}
              style={{ cursor: 'pointer' }}
            >
              Details
            </NavLink>
          </NavItem>
          {!isAdd && (
            <NavItem>
              <NavLink
                className={activeTab === '2' ? 'active' : ''}
                onClick={() => {
                  tabToogle('2');
                }}
                style={{ cursor: 'pointer' }}
              >
                Change Password
              </NavLink>
            </NavItem>
          )}
        </Nav>
        <TabContent activeTab={activeTab}>
          <TabPane tabId="1">
            <Row>
              <Col sm="12">
                <Form form="true" onSubmit={handleSubmit}>
                  <ModalBody>
                    <div className="imgUpload">
                      <img
                        className="uploadImg"
                        alt='Profile'
                        src={profileImage || defaultAvatar}
                        widht={100}
                        height={100}
                      />
                      <label
                        style={{ marginBottom: '0' }}
                        className="btn-bs-file btn btn-lg btn-danger"
                      >
                        <i className="fas fa-camera"></i>
                        <Input
                          accept=".png,.jpg,.jpeg"
                          type="file"
                          onChange={handleUpload}
                        />
                      </label>
                    </div>
                    <Row>
                      <Col md={6}>
                        <FormGroup>
                          <Label for="firstName">First Name</Label>
                          <Input
                            type="text"
                            name="firstName"
                            id="firstName"
                            placeholder="First Name"
                            invalid={
                              errors.firstName && touched.firstName
                                ? true
                                : false
                            }
                            onChange={({ target: { value } }) => {
                              setFieldTouched('firstName', true);
                              setFieldValue(
                                'firstName',
                                SingleStringFirstLetterCaps(value)
                              );
                            }}
                            value={values.firstName}
                          />
                          <FormFeedback>
                            {errors.firstName && touched.firstName
                              ? errors.firstName
                              : ''}
                          </FormFeedback>
                        </FormGroup>
                      </Col>
                      <Col md={6}>
                        <FormGroup>
                          <Label for="lastName">Last Name</Label>
                          <Input
                            type="text"
                            name="lastName"
                            id="lastName"
                            placeholder="Last Name"
                            invalid={
                              errors.lastName && touched.lastName ? true : false
                            }
                            onChange={({ target: { value } }) => {
                              setFieldTouched('lastName', true);
                              setFieldValue(
                                'lastName',
                                SingleStringFirstLetterCaps(value)
                              );
                            }}
                            value={values.lastName}
                          />
                          <FormFeedback>
                            {errors.lastName && touched.lastName
                              ? errors.lastName
                              : ''}
                          </FormFeedback>
                        </FormGroup>
                      </Col>
                      <Col md={6}>
                        <FormGroup>
                          <Label for="email">Email</Label>
                          <Input
                            type="email"
                            name="email"
                            id="email"
                            placeholder="Email"
                            disabled={isAdd ? false : true}
                            invalid={
                              errors.email && touched.email ? true : false
                            }
                            onChange={(event) => {
                              setFieldTouched('email', true);
                              handleChange(event);
                            }}
                            value={values.email}
                          />
                          <FormFeedback>
                            {errors.email && touched.email ? errors.email : ''}
                          </FormFeedback>
                        </FormGroup>
                      </Col>
                      <Col md={6}>
                        <FormGroup>
                          <Label for="phoneNumber">Phone Number</Label>
                          <InputGroup>
                            
                              <InputGroupText>+1</InputGroupText>
                            
                            <Input
                              type="text"
                              name="phoneNumber"
                              id="phoneNumber"
                              placeholder="Phone Number"
                              invalid={
                                errors.phoneNumber && touched.phoneNumber
                                  ? true
                                  : false
                              }
                              onChange={(event) => {
                                setFieldTouched('phoneNumber', true);
                                handleChange(event);
                              }}
                              value={values.phoneNumber}
                            />
                            <FormFeedback>
                              {errors.phoneNumber && touched.phoneNumber
                                ? errors.phoneNumber
                                : ''}
                            </FormFeedback>
                          </InputGroup>
                        </FormGroup>
                      </Col>
                      <Col md={12}>
                        <FormGroup>
                          <Label for="role">Role</Label>
                          <Input
                            type="select"
                            name="role"
                            id="role"
                            invalid={errors.role && touched.role ? true : false}
                            onChange={(event) => {
                              setFieldTouched('role', true);
                              handleChange(event);
                            }}
                            value={values.role}
                          >
                            <option value="">------ Select Role -------</option>
                            <option value="Merchant Admin">
                              Merchant Admin
                            </option>
                            <option value="Merchant Manager">
                              Merchant Manager
                            </option>
                          </Input>
                          <FormFeedback>
                            {errors.role && touched.role ? errors.role : ''}
                          </FormFeedback>
                        </FormGroup>
                      </Col>
                      <Col md={12}>
                        <FormGroup>
                          <Label for="address">Address</Label>
                          <Input
                            type="textarea"
                            name="address"
                            id="address"
                            placeholder="Address"
                            invalid={
                              errors.address && touched.address ? true : false
                            }
                            onChange={({ target: { value } }) => {
                              setFieldTouched('address', true);
                              setFieldValue(
                                'address',
                                SingleStringFirstLetterCaps(value)
                              );
                            }}
                            value={values.address}
                          />
                          <FormFeedback>
                            {errors.address && touched.address
                              ? errors.address
                              : ''}
                          </FormFeedback>
                        </FormGroup>
                      </Col>
                    </Row>
                  </ModalBody>
                  <ModalFooter>
                    <Button color="primary" type="submit">
                      {btnLoading && <CustomLoader loading={btnLoading} />}{' '}
                      {isAdd ? 'Create' : 'Update'}
                    </Button>
                    <Button color="secondary" onClick={toggle}>
                      Cancel
                    </Button>
                  </ModalFooter>
                </Form>
              </Col>
            </Row>
          </TabPane>
          <TabPane tabId="2">
            <Row>
              <Col sm="12">
                <AdminPasswordControl
                  email={values.email}
                  data={data}
                  toggle={toggle}
                />
              </Col>
            </Row>
          </TabPane>
        </TabContent>
      </Modal>
    </div>
  );
};

export default Create;

